import React, { useMemo, useState } from "react";
import {
  Route,
  Trash2,
  Copy,
  ClipboardPaste,
  Undo2,
  ChevronDown,
  ChevronUp,
  Coffee,
  Bed,
  Swords,
  Wind,
  X,
  Waves,
} from "lucide-react";
import Button from "../../components/ui/Button";
import {
  TERRAIN_TYPES,
  WEATHER_TYPES,
  MARINE_CONDITIONS,
  ENVIRONMENTS,
  EVENT_TYPES,
  getTerrainMod,
  getWeatherMod,
  getMarineMod,
  isMarineTerrain,
} from "./api";
import { formatDurationDays, formatDistance } from "./formatDuration";

/**
 * Compute distance in map units between two fractional points on an image of given size.
 */
export function computeDistance(a, b, imgSize, scale) {
  if (!scale?.scale_pixels || !imgSize?.w || !a || !b) return 0;
  const dxPx = (b.x - a.x) * imgSize.w;
  const dyPx = (b.y - a.y) * imgSize.h;
  const distPx = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
  return (distPx / scale.scale_pixels) * scale.scale_distance;
}

/**
 * Compute time (days) for a leg given distance and modifiers.
 * days = distance / (speed * terrainMod * conditionMod)
 * When leg.terrain is marine (water/sea), the "condition" is the leg.marineCondition,
 * otherwise it's the leg.weather.
 */
export function computeLegTime(distance, leg, travelModes, customMods) {
  const mode = travelModes.find((m) => m.id === leg.transportId) || travelModes[0];
  if (!mode || !mode.speed) return 0;
  const terrainMod = getTerrainMod(leg.terrain, customMods);
  const conditionMod = isMarineTerrain(leg.terrain)
    ? getMarineMod(leg.marineCondition, customMods)
    : getWeatherMod(leg.weather, customMods);
  const eff = mode.speed * terrainMod * conditionMod;
  return eff > 0 ? distance / eff : 0;
}

/**
 * Build initial legs from a list of points using default configuration.
 */
export function buildInitialLegs(points, travelModes) {
  if (!points || points.length < 2) return [];
  const modeId = travelModes[0]?.id || "walk";
  return points.slice(1).map(() => ({
    environment: "wilderness",
    terrain: "plain",
    weather: "clear",
    marineCondition: "calm",
    transportId: modeId,
    event: null,
  }));
}

/**
 * Duration in days of an event (encounter uses ev.hours; other types have preset hours).
 */
export function eventDurationDays(ev) {
  if (!ev || !ev.type) return 0;
  if (ev.type === "encounter") return (ev.hours || 0) / 24;
  const preset = EVENT_TYPES.find((t) => t.id === ev.type);
  return (preset?.hours || 0) / 24;
}

/* ============================================================
   PATH MANAGER (right sidebar or inline panel for draft path)
   ============================================================ */

export default function PathManager({
  draftPath,
  legs,
  travelModes,
  imgSize,
  scaleInfo,
  clipboard,
  customMods,
  onLegChange,
  onCopyLeg,
  onPasteLeg,
  onDeleteLeg,
  onRemoveLastPoint,
  onCancel,
  onSummary,
}) {
  const points = draftPath?.points || [];

  const distances = useMemo(
    () => legs.map((_, i) => computeDistance(points[i], points[i + 1], imgSize, scaleInfo)),
    [legs, points, imgSize, scaleInfo]
  );

  const legDurations = useMemo(
    () => legs.map((leg, i) => computeLegTime(distances[i], leg, travelModes, customMods)),
    [legs, distances, travelModes, customMods]
  );

  const totalTravelDays = legDurations.reduce((a, b) => a + b, 0);
  const totalEventDays = legs.reduce((a, l) => a + eventDurationDays(l?.event), 0);
  const totalDays = totalTravelDays + totalEventDays;
  const totalDistance = distances.reduce((a, b) => a + b, 0);

  return (
    <div
      className="flex flex-col h-full overflow-hidden bg-card/60 backdrop-blur border-l border-gold/20"
      data-testid="path-manager"
    >
      <div className="p-3 border-b border-gold/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Route className="w-4 h-4 text-gold" />
          <p className="font-heading text-xs tracking-widest text-gold uppercase">
            Nuovo Percorso
          </p>
        </div>
        <button
          onClick={onCancel}
          className="p-1.5 rounded-md border border-transparent text-muted-foreground hover:text-ruby hover:border-ruby/40 hover:bg-ruby/10 active:bg-ruby/20 transition-all"
          title="Chiudi"
          data-testid="path-manager-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 overflow-y-auto scrollbar-thin flex-1 space-y-3">
        {points.length < 2 ? (
          <div className="text-center py-6 space-y-2">
            <div className="w-12 h-12 mx-auto rounded-full border-2 border-dashed border-gold/40 flex items-center justify-center">
              <Route className="w-5 h-5 text-gold/60" />
            </div>
            <p className="text-xs text-muted-foreground italic px-2">
              Clicca sulla mappa per iniziare il viaggio. Ogni click aggiunge un punto.
            </p>
          </div>
        ) : (
          <>
            <div className="p-2 rounded border border-gold/30 bg-gold/5 text-xs">
              <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-1">
                Riepilogo Preliminare
              </p>
              <div className="grid grid-cols-2 gap-1 text-parchment">
                <span className="text-muted-foreground">Distanza:</span>
                <span className="text-right">{formatDistance(totalDistance, scaleInfo?.scale_unit)}</span>
                <span className="text-muted-foreground">Viaggio:</span>
                <span className="text-right">{formatDurationDays(totalTravelDays)}</span>
                <span className="text-muted-foreground">Eventi:</span>
                <span className="text-right">{formatDurationDays(totalEventDays)}</span>
                <span className="text-muted-foreground font-heading">Totale:</span>
                <span className="text-right font-heading text-gold">{formatDurationDays(totalDays)}</span>
              </div>
            </div>

            {/* Legs (each contains its own event) */}
            {legs.map((leg, i) => (
              <LegCard
                key={i}
                index={i}
                leg={leg}
                distance={distances[i]}
                duration={legDurations[i]}
                travelModes={travelModes}
                scaleUnit={scaleInfo?.scale_unit || "mi"}
                clipboard={clipboard}
                customMods={customMods}
                onChange={(patch) => onLegChange(i, patch)}
                onCopy={() => onCopyLeg(i)}
                onPaste={() => onPasteLeg(i)}
                onDelete={() => onDeleteLeg(i)}
              />
            ))}
          </>
        )}
      </div>

      <div className="p-3 border-t border-gold/20 space-y-2">
        {points.length > 0 && (
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="s"
              className="flex-1"
              onClick={onRemoveLastPoint}
              data-testid="path-remove-last-btn"
            >
              <Undo2 className="w-4 h-4 mr-1" /> Anulla ultima tappa
            </Button>
            <Button variant="secondary" size="sm" onClick={onCancel} data-testid="path-cancel-btn">
              Annulla
            </Button>
          </div>
        )}
        <Button
          variant="primary"
          size="sm"
          className="w-full"
          disabled={points.length < 2}
          onClick={onSummary}
          data-testid="open-travel-summary-btn"
        >
          Riepilogo Viaggio
        </Button>
      </div>
    </div>
  );
}

/* -------- Leg card (with embedded event editor) -------- */

function LegCard({ index, leg, distance, duration, travelModes, scaleUnit, clipboard, customMods, onChange, onCopy, onPaste, onDelete }) {
  const [open, setOpen] = useState(true);
  const mode = travelModes.find((m) => m.id === leg.transportId) || travelModes[0];
  const terrainMarine = isMarineTerrain(leg.terrain);
  const terrainMod = getTerrainMod(leg.terrain, customMods);
  const conditionMod = terrainMarine
    ? getMarineMod(leg.marineCondition, customMods)
    : getWeatherMod(leg.weather, customMods);
  const terrainLabel = (TERRAIN_TYPES.find((t) => t.id === leg.terrain) || TERRAIN_TYPES[0]).label;
  const conditionLabel = terrainMarine
    ? (MARINE_CONDITIONS.find((m) => m.id === leg.marineCondition) || MARINE_CONDITIONS[0]).label
    : (WEATHER_TYPES.find((w) => w.id === leg.weather) || WEATHER_TYPES[0]).label;

  return (
    <div className="rune-border bg-card/70 rounded-md overflow-hidden" data-testid={`leg-card-${index}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 font-heading text-[11px] tracking-widest text-gold uppercase hover:text-gold/80 transition-colors"
        >
          {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Tappa {index + 1}
          {terrainMarine && <Waves className="w-3 h-3 text-arcane" />}
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={onCopy}
            className="p-1 rounded text-muted-foreground hover:text-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
            title="Copia configurazione"
            data-testid={`leg-copy-${index}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onPaste}
            disabled={!clipboard}
            className="p-1 rounded text-muted-foreground hover:text-gold hover:bg-gold/10 active:bg-gold/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            title="Incolla configurazione"
            data-testid={`leg-paste-${index}`}
          >
            <ClipboardPaste className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded text-muted-foreground hover:text-ruby hover:bg-ruby/10 active:bg-ruby/20 transition-all"
            title="Elimina tappa"
            data-testid={`leg-delete-${index}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Read-only summary */}
      <div className="px-3 py-2 text-[11px] grid grid-cols-2 gap-x-2 gap-y-1 border-b border-border/50">
        <span className="text-muted-foreground">Ambiente:</span>
        <span className="text-parchment truncate">{ENVIRONMENTS.find((e) => e.id === leg.environment)?.label}</span>
        <span className="text-muted-foreground">Terreno:</span>
        <span className="text-parchment truncate">
          {terrainLabel} <span className="text-gold">×{terrainMod}</span>
        </span>
        <span className="text-muted-foreground">{terrainMarine ? "Condizione Marina:" : "Clima:"}</span>
        <span className="text-parchment truncate">
          {conditionLabel} <span className="text-arcane">×{conditionMod}</span>
        </span>
        <span className="text-muted-foreground">Mezzo:</span>
        <span className="text-parchment truncate">
          {mode?.name} <span className="text-gold">{mode?.speed} {scaleUnit}/g</span>
        </span>
        <span className="text-muted-foreground">Distanza:</span>
        <span className="text-parchment">{formatDistance(distance, scaleUnit)}</span>
        <span className="text-muted-foreground font-heading">Tempo:</span>
        <span className="text-gold font-heading">{formatDurationDays(duration)}</span>
      </div>

      {/* Editors */}
      {open && (
        <div className="px-3 py-2 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <select
              value={leg.environment || "wilderness"}
              onChange={(e) => onChange({ environment: e.target.value })}
              className="bg-input border border-border rounded px-1.5 py-1 text-[11px] hover:border-gold/40 focus:border-gold focus:outline-none transition-colors"
              data-testid={`leg-env-${index}`}
            >
              {ENVIRONMENTS.map((env) => (
                <option key={env.id} value={env.id}>{env.label}</option>
              ))}
            </select>
            <select
              value={leg.terrain || "plain"}
              onChange={(e) => {
                const newTerrain = e.target.value;
                const nowMarine = isMarineTerrain(newTerrain);
                const patch = { terrain: newTerrain };
                // Ensure a valid condition for the new terrain type
                if (nowMarine && !leg.marineCondition) patch.marineCondition = "calm";
                if (!nowMarine && !leg.weather) patch.weather = "clear";
                onChange(patch);
              }}
              className="bg-input border border-border rounded px-1.5 py-1 text-[11px] hover:border-gold/40 focus:border-gold focus:outline-none transition-colors"
              data-testid={`leg-terrain-${index}`}
            >
              {TERRAIN_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label} ×{getTerrainMod(t.id, customMods)}
                </option>
              ))}
            </select>
            {terrainMarine ? (
              <select
                value={leg.marineCondition || "calm"}
                onChange={(e) => onChange({ marineCondition: e.target.value })}
                className="bg-input border border-arcane/40 rounded px-1.5 py-1 text-[11px] hover:border-arcane focus:border-arcane focus:outline-none transition-colors"
                data-testid={`leg-marine-${index}`}
              >
                {MARINE_CONDITIONS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label} ×{getMarineMod(c.id, customMods)}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={leg.weather || "clear"}
                onChange={(e) => onChange({ weather: e.target.value })}
                className="bg-input border border-border rounded px-1.5 py-1 text-[11px] hover:border-gold/40 focus:border-gold focus:outline-none transition-colors"
                data-testid={`leg-weather-${index}`}
              >
                {WEATHER_TYPES.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.label} ×{getWeatherMod(w.id, customMods)}
                  </option>
                ))}
              </select>
            )}
            <select
              value={leg.transportId || travelModes[0]?.id}
              onChange={(e) => onChange({ transportId: e.target.value })}
              className="bg-input border border-border rounded px-1.5 py-1 text-[11px] hover:border-gold/40 focus:border-gold focus:outline-none transition-colors"
              data-testid={`leg-mode-${index}`}
            >
              {travelModes.filter((m) => m.enabled !== false).map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({m.speed}/g)</option>
              ))}
            </select>
          </div>

          {/* Event editor embedded in this leg */}
          <LegEventEditor
            index={index}
            event={leg.event}
            onChange={(ev) => onChange({ event: ev })}
          />
        </div>
      )}
    </div>
  );
}

/* -------- Event editor embedded inside a leg -------- */

function LegEventEditor({ index, event, onChange }) {
  const type = event?.type || "";
  const iconFor = {
    peaceful: <Wind className="w-3.5 h-3.5" />,
    short_rest: <Coffee className="w-3.5 h-3.5" />,
    long_rest: <Bed className="w-3.5 h-3.5" />,
    encounter: <Swords className="w-3.5 h-3.5" />,
  };

  return (
    <div className="border-t border-border pt-2" data-testid={`leg-event-${index}`}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="font-heading text-[9px] tracking-widest text-muted-foreground uppercase">
          Evento della Tappa
        </p>
        {type && (
          <button
            onClick={() => onChange(null)}
            className="text-[9px] text-ruby/70 hover:text-ruby font-heading tracking-widest uppercase transition-colors"
            title="Rimuovi evento"
            data-testid={`leg-event-clear-${index}`}
          >
            Rimuovi
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-1 mb-1.5">
        {EVENT_TYPES.map((et) => (
          <button
            key={et.id}
            onClick={() => onChange({ type: et.id, description: event?.description || "", hours: et.hours })}
            className={`flex items-center justify-center gap-1 py-1.5 px-1 rounded text-[10px] font-heading tracking-wider uppercase transition-all ${
              type === et.id
                ? "bg-gold/25 border border-gold text-gold shadow-inner"
                : "border border-border hover:border-gold/50 hover:bg-gold/5 text-muted-foreground"
            }`}
            data-testid={`leg-event-type-${index}-${et.id}`}
          >
            {iconFor[et.id]}
            {et.label}
          </button>
        ))}
      </div>
      {type === "encounter" && (
        <div className="space-y-1">
          <textarea
            placeholder="Descrizione incontro..."
            rows={2}
            value={event?.description || ""}
            onChange={(e) => onChange({ ...(event || {}), type: "encounter", description: e.target.value })}
            className="w-full bg-input border border-border rounded px-2 py-1 text-[11px] focus:border-gold focus:outline-none transition-colors"
            data-testid={`leg-event-desc-${index}`}
          />
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground">Durata (ore):</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={event?.hours || 0}
              onChange={(e) => onChange({ ...(event || {}), type: "encounter", hours: parseFloat(e.target.value) || 0 })}
              className="flex-1 bg-input border border-border rounded px-2 py-1 text-[11px] focus:border-gold focus:outline-none transition-colors"
              data-testid={`leg-event-hours-${index}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
